import { COLLECTOR_RULES, CORE_PHYSICS } from "../../config/gameplay";
import { clamp, normalizeDegrees } from "../../utils/math";
import { isAngleInGate, outerShell } from "../arena/shells";
import { isAngleInOverdriveZone } from "../events/overdrive";
import { tryApplyPolarityMatch, type PolarityMatchResult } from "./polarity";
import type { CollectedCoreHit, CoreState, ShellState, Vector2 } from "../runtime/types";
import type { UpgradeEffects } from "../upgrades/effects";

export interface PhysicsResult {
  readonly bounceCoreIds: readonly number[];
  readonly bounceImpacts: readonly CoreBounceImpact[];
  readonly collectedCores: readonly CollectedCoreHit[];
  readonly polarityMatches: readonly PolarityMatchResult[];
  readonly overdriveZoneHits: readonly OverdriveZoneHit[];
}

export interface CoreBounceImpact {
  readonly coreId: number;
  readonly position: Vector2;
  readonly bounceCount: number;
}

export interface OverdriveZoneHit {
  readonly coreId: number;
  readonly position: Vector2;
  readonly multiplier: number;
}

export interface CorePhysicsEventState {
  readonly overdriveActive: boolean;
  readonly overdriveZoneMultiplier: number;
}

export function advanceCores(
  cores: readonly CoreState[],
  shells: readonly ShellState[],
  deltaSeconds: number,
  effects: UpgradeEffects,
  events: CorePhysicsEventState = { overdriveActive: false, overdriveZoneMultiplier: 1 }
): PhysicsResult {
  const bounceCoreIds: number[] = [];
  const bounceImpacts: CoreBounceImpact[] = [];
  const collectedCores: CollectedCoreHit[] = [];
  const polarityMatches: PolarityMatchResult[] = [];
  const overdriveZoneHits: OverdriveZoneHit[] = [];

  for (const core of cores) {
    advanceCoreCooldowns(core, deltaSeconds);
    const substeps = substepCount(core, deltaSeconds);
    const stepSeconds = deltaSeconds / substeps;

    for (let step = 0; step < substeps; step += 1) {
      const previousPosition = { ...core.position };
      integrateCore(core, stepSeconds);
      if (core.mode === "reactor") {
        if (handleShellImpacts(core, shells, effects, events, bounceCoreIds, bounceImpacts, polarityMatches, overdriveZoneHits)) {
          continue;
        }
        updateEscapeState(core, shells);
      }

      if (core.mode === "falling" && core.position.y >= COLLECTOR_RULES.y) {
        collectedCores.push({ coreId: core.id, landingX: crossingX(previousPosition, core.position, COLLECTOR_RULES.y) });
        break;
      }
    }
  }

  return { bounceCoreIds, bounceImpacts, collectedCores, polarityMatches, overdriveZoneHits };
}

function advanceCoreCooldowns(core: CoreState, deltaSeconds: number): void {
  core.shellImpactCooldownSeconds = Math.max(0, core.shellImpactCooldownSeconds - deltaSeconds);
  core.polarityCooldownSeconds = Math.max(0, core.polarityCooldownSeconds - deltaSeconds);
}

function substepCount(core: CoreState, deltaSeconds: number): number {
  const speed = Math.hypot(core.velocity.x, core.velocity.y);
  const travel = speed * deltaSeconds;
  return clamp(Math.ceil(travel / Math.max(1, core.radius * 0.65)), 1, CORE_PHYSICS.maxSubsteps);
}

function integrateCore(core: CoreState, deltaSeconds: number): void {
  core.velocity.y += CORE_PHYSICS.gravity * deltaSeconds;
  const damping = CORE_PHYSICS.dampingPerFrame ** (deltaSeconds * 60);
  core.velocity.x *= damping;
  core.velocity.y *= damping;
  clampSpeed(core.velocity);
  core.position.x += core.velocity.x * deltaSeconds;
  core.position.y += core.velocity.y * deltaSeconds;
  core.spinRadians = normalizeSpin(core.spinRadians + (core.velocity.x * 0.018 + core.velocity.y * 0.012) * deltaSeconds);
}

function crossingX(previous: Vector2, current: Vector2, targetY: number): number {
  const deltaY = current.y - previous.y;
  if (Math.abs(deltaY) < 0.001) {
    return current.x;
  }

  const t = clamp((targetY - previous.y) / deltaY, 0, 1);
  return previous.x + (current.x - previous.x) * t;
}

function normalizeSpin(value: number): number {
  const fullTurn = Math.PI * 2;
  return ((value % fullTurn) + fullTurn) % fullTurn;
}

function handleShellImpacts(
  core: CoreState,
  shells: readonly ShellState[],
  effects: UpgradeEffects,
  events: CorePhysicsEventState,
  bounceCoreIds: number[],
  bounceImpacts: CoreBounceImpact[],
  polarityMatches: PolarityMatchResult[],
  overdriveZoneHits: OverdriveZoneHit[]
): boolean {
  let impacted = false;
  for (const shell of shells) {
    const distance = Math.hypot(core.position.x, core.position.y);
    const collisionBand = shell.thickness / 2 + core.radius;
    if (Math.abs(distance - shell.radius) > collisionBand || distance < 1) {
      continue;
    }

    const angle = normalizeDegrees((Math.atan2(core.position.y, core.position.x) * 180) / Math.PI);
    if (isAngleInGate(shell, angle)) {
      continue;
    }

    const normal = { x: core.position.x / distance, y: core.position.y / distance };
    const side = distance >= shell.radius ? 1 : -1;
    const penetration = collisionBand - Math.abs(distance - shell.radius);
    core.position.x += normal.x * penetration * side;
    core.position.y += normal.y * penetration * side;
    reflectVelocity(core, normal, shell);

    if (core.shellImpactCooldownSeconds <= 0) {
      core.bounceCount += 1;
      core.shellImpactCooldownSeconds = CORE_PHYSICS.shellImpactCooldownSeconds;
      bounceCoreIds.push(core.id);
      bounceImpacts.push({
        coreId: core.id,
        position: { ...core.position },
        bounceCount: core.bounceCount
      });
    }

    const polarityMatch = tryApplyPolarityMatch(core, shell, angle, effects, core.position);
    if (polarityMatch) {
      polarityMatches.push(polarityMatch);
    }
    if (events.overdriveActive && isAngleInOverdriveZone(shell, angle) && core.zoneMultiplier < events.overdriveZoneMultiplier) {
      core.zoneMultiplier = events.overdriveZoneMultiplier;
      overdriveZoneHits.push({
        coreId: core.id,
        position: { ...core.position },
        multiplier: events.overdriveZoneMultiplier
      });
    }
    impacted = true;
  }
  return impacted;
}

function reflectVelocity(core: CoreState, normal: Vector2, shell: ShellState): void {
  const dot = core.velocity.x * normal.x + core.velocity.y * normal.y;
  core.velocity.x = (core.velocity.x - 2 * dot * normal.x) * 0.94;
  core.velocity.y = (core.velocity.y - 2 * dot * normal.y) * 0.94;

  const tangent = { x: -normal.y, y: normal.x };
  core.velocity.x += tangent.x * shell.speedDegrees * 0.18;
  core.velocity.y += tangent.y * shell.speedDegrees * 0.18;
}

function updateEscapeState(core: CoreState, shells: readonly ShellState[]): void {
  const outsideOuterShell = Math.hypot(core.position.x, core.position.y) > outerShell(shells).radius + core.radius * 1.6;
  if (outsideOuterShell) {
    core.mode = "falling";
  }
}

function clampSpeed(velocity: Vector2): void {
  const speed = Math.hypot(velocity.x, velocity.y);
  if (speed < 0.001) {
    velocity.y = CORE_PHYSICS.minSpeed;
    return;
  }

  const nextSpeed = clamp(speed, CORE_PHYSICS.minSpeed, CORE_PHYSICS.maxSpeed);
  const scale = nextSpeed / speed;
  velocity.x *= scale;
  velocity.y *= scale;
}
